import * as React from 'react';
import { cn } from '../../../utils/cn';

/**
 * The switch's own props are omitted, not set: a spread `onClick` would
 * silently replace the toggle.
 */
export interface SwitchProps extends Omit<
  React.ComponentProps<'button'>,
  'aria-checked' | 'onChange' | 'onClick' | 'role' | 'type'
> {
  /**
   * Whether the switch is on.
   */
  checked: boolean;
  /**
   * Called with the next state when the switch is toggled.
   */
  onCheckedChange: (checked: boolean) => void;
  /**
   * Merged onto the thumb; the root carries `group`, so a thumb can key off
   * `group-aria-checked:`.
   */
  thumbClassName?: string;
}

/**
 * An on/off switch for a setting that takes effect immediately.
 */
const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  ({ checked, className, onCheckedChange, thumbClassName, ...props }, ref) => {
    const handleClick = React.useCallback(() => {
      onCheckedChange(!checked);
    }, [checked, onCheckedChange]);

    return (
      <button
        ref={ref}
        {...props}
        aria-checked={checked}
        className={cn(
          'group focus-visible:ring-ring inline-flex h-5 w-9 shrink-0 items-center rounded-full border border-transparent transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
          checked ? 'bg-primary' : 'bg-surface-4',
          className,
        )}
        role="switch"
        type="button"
        onClick={handleClick}
      >
        <span
          className={cn(
            'bg-background pointer-events-none block size-4 rounded-full shadow-lg transition-transform',
            checked ? 'translate-x-4' : 'translate-x-0.5',
            thumbClassName,
          )}
        />
      </button>
    );
  },
);
Switch.displayName = 'Switch';

export { Switch };
